import { useEffect, useState } from "react";
import { IconDownload, IconX } from "@tabler/icons-react";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useLanguage } from "../../../context/LanguageContext";

type ItemBulkResponseModalsProps = {
  onClose: () => void;
  data: {
    successful: any[];
    failed: any[];
  };
};

export default function ItemBulkResponseModals({
  onClose,
  data,
}: ItemBulkResponseModalsProps) {
  const { t } = useLanguage();
  const handleDownloadFailedCSV = async () => {
    if (data && data.failed && data.failed.length > 0) {
      try {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Failed Items");

        // Collect all unique keys except "reason"
        const allKeys = Array.from(
          new Set(data.failed.flatMap((item: any) => Object.keys(item || {})))
        ).filter((key) => key !== "reason");

        // Add "reason" as last column
        const headings = [...allKeys, "reason"];

        // Row 1: Info
        const infoRow = worksheet.addRow([
          "This file contains failed item import records. Please fix the red highlighted issues and re-upload.",
        ]);
        worksheet.mergeCells(
          `A1:${String.fromCharCode(65 + headings.length - 1)}1`
        );
        infoRow.eachCell((cell: any) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "E2EFD9" },
          };
          cell.font = { name: "Arial", bold: true, color: { argb: "212121" } };
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
        infoRow.height = 25;

        // Row 2: Validation row
        const validationTexts = headings.map((key) => {
          if (key === "itemName") return "Item Name is required";
          if (key === "category") return "Category name is required";
          if (key === "sku") return "SKU is optional";
          if (key === "commodity") return "Commodity is required";
          if (key === "import_commodity") return "Import Commodity is required";
          if (key === "dangerous_goods") return "Dangerous Goods (YES / NO) is required";
          if (key === "hs_code") return "HS Code is required";
          if (key === "storage_type") return "Storage Type (PALLETS / BINS) is required";
          if (key === "capacity") return "Capacity is required";
          if (key === "minimum_quantity") return "Minimum Quantity is required";
          if (key === "reason") return "Reason for failure";
          return `${key} is optional`;
        });

        const validationRow = worksheet.addRow(validationTexts);
        validationRow.eachCell((cell: any, colNumber: number) => {
          const key = headings[colNumber - 1];
          const isRequired = ["itemName", "category", "commodity", "import_commodity", "dangerous_goods", "hs_code", "storage_type", "capacity", "minimum_quantity"].includes(key);
          
          let fillColor = isRequired ? "FFCCCC" : "FFEB9C";
          if (key === "reason") {
            fillColor = "E2EFD9"; // light green for reason info
          }

          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: fillColor },
          };

          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };

          cell.font = { color: { argb: "212121" }, size: 9 };

          cell.border = {
            top: { style: "thin", color: { argb: "FF000000" } },
            left: { style: "thin", color: { argb: "FF000000" } },
            bottom: { style: "thin", color: { argb: "FF000000" } },
            right: { style: "thin", color: { argb: "FF000000" } },
          };
        });
        validationRow.height = 45;

        // Row 3: Headings
        const headingRow = worksheet.addRow(headings);
        headingRow.eachCell((cell: any) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "217447" },
          };
          cell.font = { name: "Arial", bold: true, color: { argb: "FFFFFF" } };
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
        headingRow.height = 25;

        // Add failed rows
        data.failed.forEach((item: any) => {
          const rowValues = headings.map((key) => item[key] ?? "");
          const row = worksheet.addRow(rowValues);

          row.eachCell((cell: any, colNumber: number) => {
            if (headings[colNumber - 1] === "reason") {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFCCCC" },
              };
            }
            cell.alignment = {
              horizontal: "center",
              vertical: "middle",
              wrapText: true,
            };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        });

        // Adjust column widths
        worksheet.columns = headings.map(() => ({ width: 22 }));

        // Export Excel
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), "failed-items.xlsx");

        toast.success(t("Failed items Excel downloaded successfully"));
      } catch (err) {
        console.error("Excel generation error:", err);
        toast.error(t("Failed to generate Excel file"));
      }
    } else {
      toast.error(t("No failed data available to download"));
    }
  };

  return (
    <div className="relative bg-white rounded-3xl shadow-sm border flex flex-col overflow-hidden h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <h2 className="text-xl font-semibold text-[#212121]">
          {t("Upload Status Report")}
        </h2>

        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-50 transition-colors"
          >
            <IconX size={20} />
          </button>
        </div>
      </div>

      <div className="space-y-6 p-6 overflow-y-auto">
        <div
          className={`grid ${
            data?.successful?.length > 0 && data?.failed?.length > 0
              ? "grid-cols-2 w-full gap-4"
              : "grid-cols-1 w-full gap-y-4"
          }`}
        >
          {data?.successful?.length > 0 && (
            <div className="flex flex-col gap-2 bg-green-500/5 rounded-xl border border-green-200 max-h-[24rem] overflow-hidden">
              <p className="text-sm text-green-800 font-semibold p-3 border-b border-green-100 bg-green-500/10">
                {t("Successfully Uploaded")} ({data.successful.length})
              </p>
              <div className="overflow-y-auto scrollbar-clean p-2">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="text-[#212121]/70 border-b border-dashed border-black/10 text-xs">
                      <th className="font-medium p-2">#</th>
                      <th className="font-medium p-2">{t("Item Name")}</th>
                      <th className="font-medium p-2">{t("Category")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.successful.map((item, index) => (
                      <tr key={index} className="text-xs text-[#212121] border-b border-black/5 hover:bg-black/5">
                        <td className="p-2">{index + 1}</td>
                        <td className="p-2 font-medium">{item.itemName}</td>
                        <td className="p-2">{item.category}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {data?.failed?.length > 0 && (
            <div className="flex flex-col gap-2 bg-red-500/5 rounded-xl border border-red-200 max-h-[24rem] overflow-hidden">
              <p className="text-sm text-red-800 font-semibold p-3 border-b border-red-100 bg-red-500/10">
                {t("Failed Records")} ({data.failed.length})
              </p>
              <div className="overflow-y-auto scrollbar-clean p-2">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="text-[#212121]/70 border-b border-dashed border-black/10 text-xs">
                      <th className="font-medium p-2">#</th>
                      <th className="font-medium p-2">{t("Item Name")}</th>
                      <th className="font-medium p-2 w-1/2">{t("Error Reason")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.failed.map((item, index) => (
                      <tr key={index} className="text-xs text-[#212121] border-b border-black/5 hover:bg-black/5">
                        <td className="p-2">{index + 1}</td>
                        <td className="p-2 font-medium">{item.itemName || "Unknown"}</td>
                        <td className="p-2 text-red-600 font-medium">
                          <span className="line-clamp-2" title={item.reason}>{item.reason}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {data?.failed?.length > 0 && (
          <div className="flex items-center justify-between gap-4 bg-yellow-50 px-4 py-3 rounded-xl border border-yellow-200">
            <p className="text-xs text-yellow-800 font-medium">
              <b>{t("Attention:")}</b> {t("We found")} {data.failed.length} {t("records that failed to upload. download the excel sheet, resolve the highlighted red errors, and re-upload.")}
            </p>
            <button
              onClick={handleDownloadFailedCSV}
              className="bg-[#212121] text-white flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap hover:bg-black/90 transition-colors shadow-sm"
            >
              <IconDownload size={18} />
              <span>{t("Download Failed Data")}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
